import React from 'react';
import { Badge, Box, Button, HStack, Text } from '@chakra-ui/react';

interface ActivityIndicatorBadgeProps {
  label?: string;
  size?: 'sm' | 'md';
  variant?: 'task' | 'project';
}

export const ActivityIndicatorBadge: React.FC<ActivityIndicatorBadgeProps> = ({
  label = 'Updated',
  size = 'sm',
  variant = 'task',
}) => {
  if (variant === 'project') {
    return (
      <Badge
        bg="blue.50"
        color="blue.700"
        borderWidth={1}
        borderColor="blue.200"
        borderRadius="full"
        px={2.5}
        py={0.5}
        fontSize="xs"
        fontWeight="medium"
      >
        <Box as="span" display="inline-block" w="6px" h="6px" borderRadius="full" bg="blue.500" mr={1.5} verticalAlign="middle" />
        {label}
      </Badge>
    );
  }

  return (
    <Badge
      size={size}
      bg="blue.600"
      color="white"
      borderRadius="full"
      px={2}
      fontWeight="semibold"
      variant="solid"
    >
      {label}
    </Badge>
  );
};

export const ActivityDot: React.FC<{ title?: string; size?: 'sm' | 'md' }> = ({
  title,
  size = 'sm',
}) => (
  <Box
    as="span"
    display="inline-block"
    w={size === 'md' ? '8px' : '6px'}
    h={size === 'md' ? '8px' : '6px'}
    borderRadius="full"
    bg="blue.400"
    flexShrink={0}
    title={title}
  />
);

interface ProjectActivityBannerProps {
  updatedCount: number;
  onMarkAllSeen: () => void;
}

export const ProjectActivityBanner: React.FC<ProjectActivityBannerProps> = ({
  updatedCount,
  onMarkAllSeen,
}) => {
  if (updatedCount <= 0) return null;

  const label =
    updatedCount === 1
      ? '1 task was updated by someone else since your last visit'
      : `${updatedCount} tasks were updated by someone else since your last visit`;

  return (
    <HStack
      mb={4}
      px={4}
      py={3}
      borderRadius="lg"
      bg="blue.50"
      borderWidth={1}
      borderColor="blue.100"
      justifyContent="space-between"
      flexWrap="wrap"
      gap={3}
    >
      <HStack gap={2}>
        <ActivityDot size="md" title={label} />
        <Box>
          <Text fontSize="sm" color="blue.900" fontWeight="semibold">
            {label}
          </Text>
          <Text fontSize="xs" color="blue.700" mt={0.5}>
            Open a task to mark it as seen, or use Mark all as seen.
          </Text>
        </Box>
      </HStack>
      <Button
        variant="outline"
        size="sm"
        colorPalette="blue"
        borderColor="blue.400"
        onClick={onMarkAllSeen}
        flexShrink={0}
      >
        Mark all as seen
      </Button>
    </HStack>
  );
};
